import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const MonthlyCalendar = () => {
  const { language } = useContext(AuthContext);
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    fetchMonthlyActivity();
  }, [currentYear, currentMonth]);

  const fetchMonthlyActivity = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/activity/monthly?year=${currentYear}&month=${currentMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthData(response.data);
    } catch (error) {
      console.error("Error fetching monthly activity:", error);
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const monthNames = language === "ca" 
    ? ["Gener", "Febrer", "Març", "Abril", "Maig", "Juny", "Juliol", "Agost", "Setembre", "Octubre", "Novembre", "Desembre"]
    : ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  const dayLabels = language === "ca"
    ? ["Dl", "Dt", "Dc", "Dj", "Dv", "Ds", "Dg"]
    : ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];

  const goToPreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    const today = new Date();
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;
    
    // Don't allow going to future months
    if (nextYear > today.getFullYear() || (nextYear === today.getFullYear() && nextMonth > today.getMonth() + 1)) {
      return;
    }
    
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getFirstDayOfWeek = () => {
    const firstDay = new Date(currentYear, currentMonth - 1, 1).getDay();
    // Convert Sunday (0) to 6, Monday (1) to 0, etc.
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const today = new Date();
  const isCurrentMonth = currentYear === today.getFullYear() && currentMonth === today.getMonth() + 1;
  const canGoNext = !(currentYear === today.getFullYear() && currentMonth === today.getMonth() + 1);

  if (loading && !monthData) {
    return (
      <div className="glass rounded-2xl p-6 border border-purple-400/10">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700/50 rounded mb-4 w-1/2 mx-auto" />
          <div className="grid grid-cols-7 gap-1">
            {[...Array(35)].map((_, i) => (
              <div key={i} className="h-8 bg-slate-700/30 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const activeDays = monthData?.days?.filter(d => d.has_activity).length || 0;
  const totalDays = monthData?.days?.length || 30;

  return (
    <div data-testid="monthly-calendar" className="glass rounded-2xl p-6 border border-purple-400/10">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={goToPreviousMonth}
          className="p-2 text-gray-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          data-testid="prev-month-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-300" />
          <h3 className="text-lg font-semibold text-gray-200">
            {monthNames[currentMonth - 1]} {currentYear}
          </h3>
        </div>
        
        <button
          onClick={goToNextMonth}
          disabled={!canGoNext}
          className={`p-2 rounded-lg transition-colors ${
            canGoNext 
              ? 'text-gray-400 hover:text-white hover:bg-slate-800' 
              : 'text-slate-700 cursor-not-allowed'
          }`}
          data-testid="next-month-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayLabels.map(day => (
          <div key={day} className="text-center text-xs text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before the 1st */}
        {[...Array(getFirstDayOfWeek())].map((_, i) => (
          <div key={`empty-${i}`} className="h-9" />
        ))}
        
        {/* Day cells */}
        {monthData?.days?.map((day, index) => (
          <div
            key={day.date}
            data-testid={`calendar-day-${day.day_number}`}
            className={`
              h-9 flex items-center justify-center text-sm rounded-lg transition-all
              ${day.is_today 
                ? 'ring-2 ring-purple-400 ring-offset-1 ring-offset-slate-900' 
                : ''}
              ${day.has_activity 
                ? 'bg-gradient-to-br from-purple-400/80 to-purple-600/80 text-white font-medium' 
                : 'text-gray-500 hover:bg-slate-800/50'}
            `}
          >
            {day.day_number}
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="mt-4 pt-4 border-t border-slate-800 flex justify-between items-center">
        <span className="text-sm text-gray-400">
          {t("Días activos este mes:", "Dies actius aquest mes:")}
        </span>
        <span className="text-sm font-semibold text-purple-300">
          {activeDays}/{totalDays}
        </span>
      </div>
    </div>
  );
};

export default MonthlyCalendar;
